import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ExternalLink, Eye, Copy, CheckCircle, RefreshCw, AlertCircle, Loader2, DollarSign } from 'lucide-react';
import { useProducts } from '@/hooks/useProducts';
import { useAllProducts } from '@/hooks/useAllProducts';
import { fetchProductByHandle } from '@/services/shopify';
import { PRODUCT_MAPPINGS } from '@/types/shopify';
import type { ShopifyProduct, ShopifyBundle } from '@/types/shopify';

/**
 * @deprecated This component is for testing individual product selection which is being phased out.
 * Component is kept for development/testing purposes only.
 * Individual product selection has been replaced by kit-based selection.
 * This component should not be used in production and will be removed in a future version.
 */
export default function ShopifyProductTester() {
  // Development-only deprecation warning
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'ShopifyProductTester component is deprecated and should only be used for development/testing. ' +
      'Individual product selection has been replaced by kit-based selection. ' +
      'This component will be removed in a future version.'
    );
  }

  const { equipos, mascarillas, isLoading, error, refetch } = useProducts();
  const { allProducts, bundles, individualProducts, isLoading: allProductsLoading, error: allProductsError, refetch: refetchAllProducts } = useAllProducts();
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const [loadingHandle, setLoadingHandle] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const fetchShopifyProduct = async (handle: string) => {
    setLoadingHandle(handle);
    try {
      const product = await fetchProductByHandle(handle);
      setSelectedProduct(product);
    } catch (error) {
      console.error('Error fetching product:', error);
      alert('Failed to fetch product from Shopify');
    } finally {
      setLoadingHandle(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const formatPrice = (amount: string, currencyCode: string = 'MXN') => {
    const price = parseFloat(amount);
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (isLoading || allProductsLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading products from Shopify...</p>
          {allProductsLoading && <p className="text-xs text-muted-foreground">Fetching all products for bundle analysis...</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Shopify Product Tester
              </CardTitle>
              <p className="text-muted-foreground mt-1">
                Test dynamic product pricing integration with Shopify Storefront API
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Products
              </Button>
              <Button onClick={() => refetchAllProducts()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Environment Status */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-3">
              {import.meta.env.VITE_SHOPIFY_STORE_DOMAIN && import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-700">Shopify Integration Configured</p>
                    <p className="text-sm text-muted-foreground">
                      Store: {import.meta.env.VITE_SHOPIFY_STORE_DOMAIN} | 
                      API Version: {import.meta.env.VITE_SHOPIFY_API_VERSION || '2024-01'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-700">Using Fallback Data</p>
                    <p className="text-sm text-muted-foreground">
                      Configure VITE_SHOPIFY_STORE_DOMAIN and VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN to test live data
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {(error || allProductsError) && (
            <div className="space-y-2">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700">Product API Error (Using Fallback)</p>
                      <p className="text-sm text-red-600">{error.message}</p>
                    </div>
                  </div>
                </div>
              )}
              {allProductsError && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-700">All Products API Error</p>
                      <p className="text-sm text-amber-600">{allProductsError.message}</p>
                      <p className="text-xs text-amber-600 mt-1">Bundle analysis not available</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bundle Products Section */}
          {bundles.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                🎁 Bundle Products ({bundles.length})
                <Badge variant="secondary" className="text-xs">Detected from Shopify</Badge>
              </h3>

              {/* Quick Variant IDs Reference */}
              <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  🛒 Quick Cart Reference - All Bundle Variant IDs
                </h4>
                <div className="space-y-3">
                  {bundles.map((bundle, bundleIdx) => (
                    <div key={bundleIdx} className="space-y-1">
                      <p className="font-medium text-sm text-indigo-800">{bundle.title}</p>
                      <div className="flex flex-wrap gap-2">
                        {bundle.variants.map((variant, variantIdx) => (
                          <div key={variantIdx} className="flex items-center gap-1 bg-white px-2 py-1 rounded border">
                            <span className="text-xs text-gray-600">{variant.title}:</span>
                            <code className="text-xs font-mono text-indigo-600">{variant.id}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0"
                              onClick={() => copyToClipboard(variant.id)}
                            >
                              {copiedText === variant.id ? (
                                <CheckCircle className="h-3 w-3 text-green-500" />
                              ) : (
                                <Copy className="h-2 w-2" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-indigo-600 mt-3 italic">
                  💡 Use these variant IDs when creating cart line items for Shopify checkout
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bundles.map((bundle, index) => (
                  <Card key={`bundle-${index}`} className="relative border-primary/20">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="w-16 h-16 bg-white rounded-lg border overflow-hidden flex-shrink-0">
                          {bundle.images.length > 0 ? (
                            <img 
                              src={bundle.images[0].url} 
                              alt={bundle.title}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted/20 flex items-center justify-center">
                              <ExternalLink className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-primary">{bundle.title}</h4>
                          <p className="text-2xl font-bold text-green-600">
                            ${bundle.price.toLocaleString()}
                          </p>
                          <div className="flex gap-1 mt-1">
                            <Badge variant="outline" className="text-xs">Bundle</Badge>
                            {bundle.variants.length > 0 && (
                              <Badge variant="outline" className="text-xs">{bundle.variants.length} variants</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Separator className="my-3" />
                      
                      <div className="space-y-2">
                        {bundle.equipoComponent && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Equipment:</span>
                            <code className="text-xs bg-green-100 px-1 py-0.5 rounded">
                              {bundle.equipoComponent}
                            </code>
                          </div>
                        )}
                        
                        {bundle.mascarillaComponent && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Mask:</span>
                            <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">
                              {bundle.mascarillaComponent}
                            </code>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Shopify Handle:</span>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {bundle.handle}
                          </code>
                        </div>
                        
                        {/* Variant IDs for Cart Generation */}
                        {bundle.variants.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground font-medium">🛒 Variant IDs for Cart:</span>
                            </div>
                            <div className="space-y-1">
                              {bundle.variants.map((variant, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded border border-purple-200">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-purple-800">{variant.title}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge variant={variant.availableForSale ? "default" : "secondary"} className="text-xs">
                                        {variant.availableForSale ? 'Available' : 'Unavailable'}
                                      </Badge>
                                      <span className="text-sm font-semibold text-green-700">
                                        {formatPrice(variant.price.amount, variant.price.currencyCode)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => copyToClipboard(variant.id)}
                                    >
                                      {copiedText === variant.id ? (
                                        <CheckCircle className="h-3 w-3 text-green-500" />
                                      ) : (
                                        <Copy className="h-3 w-3" />
                                      )}
                                    </Button>
                                    <code className="text-xs bg-purple-100 px-2 py-1 rounded font-mono max-w-[200px] truncate" title={variant.id}>
                                      {variant.id}
                                    </code>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="text-xs text-muted-foreground italic">
                              💡 Copy variant IDs above to use in cart generation
                            </div>
                          </div>
                        )}
                        
                        <div className="flex gap-2 mt-3">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => fetchShopifyProduct(bundle.handle)}
                                disabled={loadingHandle === bundle.handle}
                              >
                                {loadingHandle === bundle.handle ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Eye className="h-3 w-3 mr-1" />
                                )}
                                View Details
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Bundle Details - {bundle.title}</DialogTitle>
                                <DialogDescription>
                                  Shopify bundle product with {bundle.variants.length} variants
                                </DialogDescription>
                              </DialogHeader>
                              {selectedProduct && (
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Title</label>
                                      <p className="text-sm">{selectedProduct.title}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Product Type</label>
                                      <p className="text-sm font-mono">{selectedProduct.productType}</p>
                                    </div>
                                  </div>
                                  
                                  {selectedProduct.tags.length > 0 && (
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Tags</label>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {selectedProduct.tags.map((tag, idx) => (
                                          <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div>
                                    <label className="text-sm font-medium text-muted-foreground">Price Range</label>
                                    <p className="text-lg font-bold text-primary">
                                      {formatPrice(selectedProduct.priceRange.minVariantPrice.amount, selectedProduct.priceRange.minVariantPrice.currencyCode)}
                                    </p>
                                  </div>

                                  {selectedProduct.variants.edges.length > 0 && (
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Variants ({selectedProduct.variants.edges.length})</label>
                                      <div className="space-y-2 mt-2">
                                        {selectedProduct.variants.edges.map(({ node: variant }, idx) => (
                                          <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                            <div>
                                              <p className="text-sm font-medium">{variant.title}</p>
                                              <div className="flex gap-2">
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-6 px-2 text-xs"
                                                  onClick={() => copyToClipboard(variant.id)}
                                                >
                                                  {copiedText === variant.id ? (
                                                    <CheckCircle className="h-3 w-3" />
                                                  ) : (
                                                    <Copy className="h-3 w-3" />
                                                  )}
                                                </Button>
                                                <code className="text-xs text-muted-foreground">{variant.id}</code>
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <p className="text-sm font-semibold">
                                                {formatPrice(variant.price.amount, variant.price.currencyCode)}
                                              </p>
                                              <Badge variant={variant.availableForSale ? "default" : "secondary"} className="text-xs">
                                                {variant.availableForSale ? 'Available' : 'Unavailable'}
                                              </Badge>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(JSON.stringify(bundle, null, 2))}
                          >
                            {copiedText === JSON.stringify(bundle, null, 2) ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <Copy className="h-3 w-3 mr-1" />
                            )}
                            Copy JSON
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* All Products Summary */}
          {allProducts.length > 0 && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Store Overview</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-blue-700 font-medium">{allProducts.length}</p>
                  <p className="text-blue-600">Total Products</p>
                </div>
                <div>
                  <p className="text-green-700 font-medium">{bundles.length}</p>
                  <p className="text-green-600">Bundles Detected</p>
                </div>
                <div>
                  <p className="text-purple-700 font-medium">{individualProducts.length}</p>
                  <p className="text-purple-600">Individual Products</p>
                </div>
                <div>
                  <p className="text-orange-700 font-medium">{PRODUCT_MAPPINGS.length}</p>
                  <p className="text-orange-600">Mapped Products</p>
                </div>
              </div>
            </div>
          )}

          {/* CPAP Equipment Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              CPAP Equipment ({equipos.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {equipos.map((equipo, index) => {
                const mapping = PRODUCT_MAPPINGS.find(m => 
                  m.type === 'equipo' && 
                  m.internalId.includes(equipo.marca.toLowerCase())
                );
                
                return (
                  <Card key={`equipo-${index}`} className="relative">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="w-16 h-16 bg-white rounded-lg border overflow-hidden flex-shrink-0">
                          <img 
                            src={equipo.imagen} 
                            alt={equipo.modelo}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{equipo.marca} {equipo.modelo}</h4>
                          <p className="text-sm text-muted-foreground">
                            Pricing via bundle only
                          </p>
                          <div className="flex gap-1 mt-2">
                            <Badge variant="outline" className="text-xs">{equipo.conectividad}</Badge>
                            <Badge variant="outline" className="text-xs">{equipo.garantia}</Badge>
                          </div>
                        </div>
                      </div>
                      
                      <Separator className="my-3" />
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Shopify Handle:</span>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {mapping?.shopifyHandle || 'not-mapped'}
                          </code>
                        </div>
                        
                        <div className="flex gap-2">
                          {mapping && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => fetchShopifyProduct(mapping.shopifyHandle)}
                                  disabled={loadingHandle === mapping.shopifyHandle}
                                >
                                  {loadingHandle === mapping.shopifyHandle ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Eye className="h-3 w-3 mr-1" />
                                  )}
                                  View Shopify Data
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Shopify Product Details</DialogTitle>
                                  <DialogDescription>
                                    Live data from Shopify Storefront API
                                  </DialogDescription>
                                </DialogHeader>
                                {selectedProduct && (
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Title</label>
                                        <p className="text-sm">{selectedProduct.title}</p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Handle</label>
                                        <p className="text-sm font-mono">{selectedProduct.handle}</p>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Price Range</label>
                                      <p className="text-lg font-bold text-primary">
                                        {formatPrice(selectedProduct.priceRange.minVariantPrice.amount, selectedProduct.priceRange.minVariantPrice.currencyCode)}
                                      </p>
                                    </div>

                                    {selectedProduct.variants.edges.length > 0 && (
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Variants ({selectedProduct.variants.edges.length})</label>
                                        <div className="space-y-2 mt-2">
                                          {selectedProduct.variants.edges.map(({ node: variant }, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                              <div>
                                                <p className="text-sm font-medium">{variant.title}</p>
                                                <div className="flex gap-2">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => copyToClipboard(variant.id)}
                                                  >
                                                    {copiedText === variant.id ? (
                                                      <CheckCircle className="h-3 w-3" />
                                                    ) : (
                                                      <Copy className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                  <code className="text-xs text-muted-foreground">{variant.id}</code>
                                                </div>
                                              </div>
                                              <div className="text-right">
                                                <p className="text-sm font-semibold">
                                                  {formatPrice(variant.price.amount, variant.price.currencyCode)}
                                                </p>
                                                <Badge variant={variant.availableForSale ? "default" : "secondary"} className="text-xs">
                                                  {variant.availableForSale ? 'Available' : 'Unavailable'}
                                                </Badge>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {selectedProduct.images.edges.length > 0 && (
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Images</label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                                          {selectedProduct.images.edges.slice(0, 6).map(({ node: image }, idx) => (
                                            <div key={idx} className="aspect-square bg-muted rounded border overflow-hidden">
                                              <img 
                                                src={image.url} 
                                                alt={image.altText || `Image ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(JSON.stringify(equipo, null, 2))}
                          >
                            {copiedText === JSON.stringify(equipo, null, 2) ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <Copy className="h-3 w-3 mr-1" />
                            )}
                            Copy JSON
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Masks Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Masks / Mascarillas ({mascarillas.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mascarillas.map((mascarilla, index) => {
                const mapping = PRODUCT_MAPPINGS.find(m => 
                  m.type === 'mascarilla' && 
                  m.internalId.includes(mascarilla.marca.toLowerCase())
                );
                
                return (
                  <Card key={`mascarilla-${index}`} className="relative">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="w-16 h-16 bg-white rounded-lg border overflow-hidden flex-shrink-0">
                          <img 
                            src={mascarilla.imagen} 
                            alt={mascarilla.modelo}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{mascarilla.marca} {mascarilla.modelo}</h4>
                          <p className="text-sm text-muted-foreground">
                            Pricing via bundle only
                          </p>
                          <Badge variant="outline" className="text-xs mt-1">{mascarilla.tipo}</Badge>
                        </div>
                      </div>
                      
                      <Separator className="my-3" />
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Shopify Handle:</span>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {mapping?.shopifyHandle || 'not-mapped'}
                          </code>
                        </div>
                        
                        <div className="flex gap-2">
                          {mapping && (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => fetchShopifyProduct(mapping.shopifyHandle)}
                                  disabled={loadingHandle === mapping.shopifyHandle}
                                >
                                  {loadingHandle === mapping.shopifyHandle ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Eye className="h-3 w-3 mr-1" />
                                  )}
                                  View Shopify Data
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Shopify Product Details</DialogTitle>
                                  <DialogDescription>
                                    Live data from Shopify Storefront API for {mapping.shopifyHandle}
                                  </DialogDescription>
                                </DialogHeader>
                                {selectedProduct && (
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Title</label>
                                        <p className="text-sm">{selectedProduct.title}</p>
                                      </div>
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Handle</label>
                                        <p className="text-sm font-mono">{selectedProduct.handle}</p>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Price Range</label>
                                      <p className="text-lg font-bold text-primary">
                                        {formatPrice(selectedProduct.priceRange.minVariantPrice.amount, selectedProduct.priceRange.minVariantPrice.currencyCode)}
                                      </p>
                                    </div>

                                    {selectedProduct.variants.edges.length > 0 && (
                                      <div>
                                        <label className="text-sm font-medium text-muted-foreground">Variants ({selectedProduct.variants.edges.length})</label>
                                        <div className="space-y-2 mt-2">
                                          {selectedProduct.variants.edges.map(({ node: variant }, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                                              <div>
                                                <p className="text-sm font-medium">{variant.title}</p>
                                                <div className="flex gap-2">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => copyToClipboard(variant.id)}
                                                  >
                                                    {copiedText === variant.id ? (
                                                      <CheckCircle className="h-3 w-3" />
                                                    ) : (
                                                      <Copy className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                  <code className="text-xs text-muted-foreground">{variant.id}</code>
                                                </div>
                                              </div>
                                              <div className="text-right">
                                                <p className="text-sm font-semibold">
                                                  {formatPrice(variant.price.amount, variant.price.currencyCode)}
                                                </p>
                                                <Badge variant={variant.availableForSale ? "default" : "secondary"} className="text-xs">
                                                  {variant.availableForSale ? 'Available' : 'Unavailable'}
                                                </Badge>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          )}
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(JSON.stringify(mascarilla, null, 2))}
                          >
                            {copiedText === JSON.stringify(mascarilla, null, 2) ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <Copy className="h-3 w-3 mr-1" />
                            )}
                            Copy JSON
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Product Mappings Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Product Mappings Configuration</h3>
            <div className="space-y-2">
              {PRODUCT_MAPPINGS.map((mapping, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{mapping.internalId}</p>
                    <p className="text-xs text-muted-foreground">{mapping.type}</p>
                  </div>
                  <div className="text-right">
                    <code className="text-xs bg-background px-2 py-1 rounded border">
                      {mapping.shopifyHandle}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}